<template>
    <div>
        <div class="is-clearfix">
            <div class="chart-title is-pulled-left">
                <h1 class="title">
                    {{title}}
                    <b-icon class="tooltip-info is-small"
                            v-if="!zoom"
                            @mouseover.native="toggleHelp(true)"
                            @mouseleave.native="toggleHelp(false)"
                            pack="far"
                            type="is-info"
                            icon="question-circle"
                            size="is-small">
                    </b-icon>
                </h1>
                <h2 class="subtitle">{{subtitle}}</h2>
            </div>

            <div class="chart-toolbar is-pulled-right level">
                <b-field class="is-middle" style="margin:0 5px 0 0">
                    <b-checkbox
                        size="is-small"
                        v-model="zoom"
                        native-value="false"
                        @input="changeZoom">
                        Scale to fit
                    </b-checkbox>
                </b-field>

                <b-select class="is-pulled-left" size="is-small" v-model="selectedDisplayPeriod" v-if="availableDisplayPeriods.length">
                    <option v-for="displayPeriod in availableDisplayPeriods" :value="displayPeriod" :key="displayPeriod.key">
                        {{ displayPeriod.value }}
                    </option>
                </b-select>

                <button class="button is-small" @click="exportPPTX" :disabled="isExporting || !project.metrics.length">
                    <b-icon icon="file-powerpoint"
                            size="is-small"
                            pack="far">
                    </b-icon>
                    <span>Export</span>
                </button>
            </div>
        </div>
        <hr>
        <chart ref="chart" class="chart-content" :chartData="chartData"/>
        <div class="slider-container">
            <b-field>
                <b-slider
                    v-model="selectedDisplayPeriodIndex"
                    :min="0"
                    :max="availableDisplayPeriods.length-1"
                    :custom-formatter="val => selectedDisplayPeriod.value"
                    :ticks="true"
                    type="is-grey-slider"
                    rounded
                    >
                </b-slider>
            </b-field>
        </div>
        <chart-legend :legends="chartData.settings.series" />
        <div style="clear: both"></div>
    </div>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';

import { exporterService } from '@/services/exporter-service';
import { chartLabelGeneratorService } from '@/services/chart-label-generator-service';
import { snapshotChartHelperService } from '@/services/snapshot-chart-helper-service';
import { finalScoreSwotRequestor } from '@/requestors/final-score-swot-requestor.js';
import { finalScoreSnapshotRequestor } from '@/requestors/final-score-snapshot-requestor.js';

import Chart from '@/components/charts/Chart.vue';
import ChartLegend from '@/components/charts/ChartLegend.vue';
import { chartTypes } from '@/components/charts/common/chartTypes';
import constants from '@/constants.js';

var locks = {}; // hold mutex locks in this object

export default {
    name: 'FinalScoreSwot',
    components: {
        Chart,
        ChartLegend
    },
    props: {
        project: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            title: 'Pillar Score SWOT',
            subtitle: '',
            chartData: {
                chartType: chartTypes.SWOT,
                settings: {
                    xAxis: {
                        label: 'Conversation'
                    },
                    yAxis: {
                        label: 'Quality',
                        ticks: 5
                    },
                    series: []
                },
                meta: {
                    metric: ['xConversation', 'yQuality', 'zCommunity'],
                    series: 'columnId'
                },
                data: []
            },
            availableDisplayPeriods: [],
            selectedDisplayPeriod: '',
            isExporting: false,
            zoom: false,
            selectedDisplayPeriodIndex: 0
        };
    },
    watch: {
        'project.metrics': function (newMetrics) {
            this.availableDisplayPeriods = snapshotChartHelperService.buildAvailableDisplayPeriods(newMetrics, this.project.averageMetricsForEntireRange,
                this.project.meta.period, this.project.meta.daySmoothing);
            if (this.availableDisplayPeriods && this.availableDisplayPeriods.length > 0) {
                if (this.selectedDisplayPeriod === this.availableDisplayPeriods[0]) {
                    this.loadMetricsBasedOnSelectedPeriod();
                } else {
                    this.selectedDisplayPeriod = this.availableDisplayPeriods[0];
                    this.selectedDisplayPeriodIndex = this.availableDisplayPeriods.length - 1;
                }
            }
        },
        'project.settings.columns': {
            handler: function (newColumns) {
                this.chartData.settings.series = _(newColumns)
                    .filter(column => column.enabled)
                    .map(column => ({
                        id: column.id,
                        name: column.name,
                        colour: column.colour,
                        visible: true
                    }))
                    .value();
            },
            deep: true
        },
        selectedDisplayPeriod: function () {
            this.loadMetricsBasedOnSelectedPeriod();

            if (!locks.selectedDisplayPeriod) {
                locks.selectedDisplayPeriod = true;
                let indexOfSelectedDisplayPeriod = _.indexOf(this.availableDisplayPeriods, this.selectedDisplayPeriod);
                let reversedIndex = this.reverseIndex(indexOfSelectedDisplayPeriod);
                this.selectedDisplayPeriodIndex = reversedIndex;
                // need to wait for next tick before disabling lock mutex, so that watch has a chance to fire
                Vue.nextTick(function () {
                    locks.selectedDisplayPeriod = false;
                });
            }
        },
        selectedDisplayPeriodIndex: function (newIndex) {
            if (!locks.selectedDisplayPeriod) {
                let reversedIndex = this.reverseIndex(newIndex);
                locks.selectedDisplayPeriod = true;
                this.selectedDisplayPeriod = this.availableDisplayPeriods[reversedIndex];

                // need to wait for next tick before disabling lock mutex, so that watch has a chance to fire
                Vue.nextTick(function () {
                    locks.selectedDisplayPeriod = false;
                });
            }
        }
    },
    methods: {
        changeZoom () {
            this.$refs.chart.interact.emitToggleZoom(this.zoom);
        },
        async exportPPTX () {
            this.isExporting = true;
            let pptxRequest = this.zoom
                ? finalScoreSnapshotRequestor.generatePPTXRequest(this.chartData.data, this.title, this.subtitle, this.chartData.settings)
                : finalScoreSwotRequestor.generatePPTXRequest(this.chartData.data, this.title, this.subtitle, this.chartData.settings);

            try {
                await exporterService.exportPPTX(pptxRequest);
            } catch (error) {
                this.$buefy.toast.open({
                    message: 'Failed to export ' + this.title + ' Chart',
                    type: 'is-danger'
                });
            } finally {
                this.isExporting = false;
            }
        },
        toggleHelp (enabled) {
            this.$refs.chart.interact.emitToggleHelp(enabled);
        },
        loadMetricsBasedOnSelectedPeriod () {
            if (this.selectedDisplayPeriod.key === snapshotChartHelperService.AVG_METRICS_KEY) {
                this.chartData.data = this.project.averageMetricsForEntireRange;
                this.subtitle = this.selectedDisplayPeriod.value;
            } else {
                const metrics = this.project.metrics.filter(m => m.periodName === this.selectedDisplayPeriod.key);

                if (metrics && metrics.length > 0) {
                    let strPattern = chartLabelGeneratorService.generateSnapChartLabelPattern(this.project.meta.period, this.project.meta.daySmoothing);
                    this.subtitle = (this.project.meta.period.key === constants.PERIOD.DAY || this.project.meta.period.key === constants.PERIOD.WEEK)
                        ? chartLabelGeneratorService.formatString(strPattern, metrics[0].periodStartDate, metrics[0].periodEndDate)
                        : chartLabelGeneratorService.formatString(strPattern, metrics[0].periodName);
                }
                this.chartData.data = metrics;
            }
        },
        reverseIndex (index) {
            return (this.availableDisplayPeriods.length - 1) - index;
        }
    },
    mounted () {
        let chartDetail = {
            getSlideRequest: () => {
                let pptxRequest = this.zoom
                    ? finalScoreSnapshotRequestor.getSlideRequest(this.chartData.data, this.title, this.subtitle, this.chartData.settings)
                    : finalScoreSwotRequestor.getSlideRequest(this.chartData.data, this.title, this.subtitle, this.chartData.settings);
                return pptxRequest;
            }
        };
        this.$emit('on-chart-mount-complete', chartDetail);
    }
};
</script>

<style lang="scss" scoped>
    .chart-toolbar {
        .icon {
            padding-left: 5px;
        }
    }
    .chart-content {
        width: 100%;
        height: 300px;
    }
    .title {
        font-size: 16px;

        .tooltip-info {
            cursor: pointer;

            &:hover {
                opacity: 0.4;
            }
        }
    }
    .subtitle {
        font-size: 14px;
        margin-bottom: 5px;
    }
    hr {
        margin: 5px;
    }
</style>
